import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { recruitingAPI } from "../../services/recruitingApi";

interface OtherApplication {
  id: number;
  status: string;
  job_title: string;
  job_id: number;
}

interface SuccessMetadata {
  sibling_rejections_count?: number;
  candidate_hired?: boolean;
  candidate_hire_skipped?: boolean;
  candidate_hire_skip_reason?: string;
  candidate_current_status?: string;
  candidate_already_hired?: boolean;
  candidate_other_active_applications?: OtherApplication[];
  candidate_open_for_more_projects?: boolean;
  job_status_suggestion?: string;
  job_current_status?: string;
}

interface SuccessConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  metadata: SuccessMetadata;
  jobTitle?: string;
  candidateName?: string;
  onJobStatusUpdate?: (jobId: number) => void;
}

const SuccessConfirmationDialog: React.FC<SuccessConfirmationDialogProps> = ({
  open,
  onClose,
  metadata,
  jobTitle,
  candidateName,
  onJobStatusUpdate,
}) => {
  const [otherAppsAction, setOtherAppsAction] = useState<string>(
    metadata.candidate_open_for_more_projects ? "keep" : "reject",
  );
  const [processing, setProcessing] = useState(false);

  const handleRejectOtherApps = async () => {
    if (!metadata.candidate_other_active_applications) return;

    setProcessing(true);
    try {
      const ids = metadata.candidate_other_active_applications.map((a) => a.id);
      await recruitingAPI.bulkRejectCandidateApplications(
        ids,
        "candidate_withdrew",
        `Candidate hired for ${jobTitle || "another position"}`,
      );
      onClose();
    } catch (error) {
      console.error("Error rejecting other applications:", error);
    } finally {
      setProcessing(false);
    }
  };

  const hasOtherApps =
    metadata.candidate_other_active_applications &&
    metadata.candidate_other_active_applications.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Application Marked as Success</DialogTitle>
      <DialogContent>
        {/* US1: Sibling rejections */}
        {metadata.sibling_rejections_count != null &&
          metadata.sibling_rejections_count > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {metadata.sibling_rejections_count} other application
              {metadata.sibling_rejections_count === 1 ? " was" : "s were"}{" "}
              auto-rejected for this job (position filled).
            </Alert>
          )}

        {/* US3: Candidate hire feedback */}
        {metadata.candidate_hired && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {candidateName || "Candidate"} was automatically transitioned to
            Hired.
          </Alert>
        )}

        {metadata.candidate_hire_skipped && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Application marked as success, but{" "}
            {candidateName || "the candidate"} was not auto-transitioned to
            Hired because they are in &apos;{metadata.candidate_current_status}
            &apos; status.
            {metadata.candidate_current_status === "to_come_back" && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Consider transitioning the candidate to &apos;Ready for
                Sale&apos; first.
              </Typography>
            )}
          </Alert>
        )}

        {metadata.candidate_already_hired && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {candidateName || "Candidate"} is already in Hired status.
          </Alert>
        )}

        {/* US4: Job status suggestion */}
        {metadata.job_status_suggestion && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              onJobStatusUpdate && (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => onJobStatusUpdate(0)}
                >
                  Update
                </Button>
              )
            }
          >
            Position filled -- consider marking the job as
            &quot;Delivered&quot;.
          </Alert>
        )}

        {/* US2: Other active applications */}
        {hasOtherApps && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              {candidateName || "This candidate"} has{" "}
              {metadata.candidate_other_active_applications!.length} other
              active application
              {metadata.candidate_other_active_applications!.length === 1
                ? ""
                : "s"}
              :
            </Typography>

            <List dense disablePadding>
              {metadata.candidate_other_active_applications!.map((app) => (
                <ListItem key={app.id} disablePadding sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={app.job_title || `Job #${app.job_id}`}
                    secondary={
                      <Chip
                        label={app.status.replace(/_/g, " ")}
                        size="small"
                        sx={{ fontSize: "11px", height: "20px" }}
                      />
                    }
                  />
                </ListItem>
              ))}
            </List>

            <RadioGroup
              value={otherAppsAction}
              onChange={(e) => setOtherAppsAction(e.target.value)}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="reject"
                control={<Radio size="small" />}
                label="Reject all other applications"
              />
              <FormControlLabel
                value="keep"
                control={<Radio size="small" />}
                label="Keep open (multi-project candidate)"
              />
            </RadioGroup>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {hasOtherApps && otherAppsAction === "reject" ? (
          <>
            <Button onClick={onClose} disabled={processing}>
              Cancel
            </Button>
            <Button
              onClick={handleRejectOtherApps}
              variant="contained"
              color="warning"
              disabled={processing}
            >
              {processing ? "Rejecting..." : "Reject Other Applications"}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="contained">
            OK
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SuccessConfirmationDialog;
